import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  PBMConnection,
  UpdatePBMConnectionDTO,
  CreatePBMConnectionDTO,
  getConnectionsByPbmId,
  createConnection,
  updateConnection,
  updateConnectionCredentials,
  deleteConnection
} from '../../services/pbmConnectionService';
import { PBMOutputMapping, getAllOutputMappings } from '../../services/pbmOutputMappingService';
import { getCurrentUser } from '../../services/userService';
import { SftpFileRetrievalResult, retrieveFilesByConnectionId } from '../../services/sftpFileRetrievalService';

interface PBMConnectionTableProps {
  pbmId: number;
  onFilesRetrieved?: (result: SftpFileRetrievalResult) => void;
}

interface ConnectionFormData {
  ipAddress: string;
  userName: string;
  port: number;
  keyLocation?: string;
  outputMappingId: number;
  authType: 'Password' | 'PrivateKey';
  password?: string;
  keyPassphrase?: string;
  remoteDestinationDirectory: string;
  remoteSourceDirectory: string;
  destinationContainerName: string;
}

const initialFormData: ConnectionFormData = {
  ipAddress: '',
  userName: '',
  port: 22,
  keyLocation: '',
  outputMappingId: 0,
  authType: 'Password',
  password: '',
  keyPassphrase: '',
  remoteDestinationDirectory: '',
  remoteSourceDirectory: '',
  destinationContainerName: ''
};

const PBMConnectionTable = ({ pbmId, onFilesRetrieved }: PBMConnectionTableProps) => {
  const [connections, setConnections] = useState<PBMConnection[]>([]);
  const [outputMappings, setOutputMappings] = useState<PBMOutputMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<PBMConnection | null>(null);
  const [editingConnection, setEditingConnection] = useState<PBMConnection | null>(null);
  const [formData, setFormData] = useState<ConnectionFormData>(initialFormData);
  //const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [feedback, setFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [fetchingFiles, setFetchingFiles] = useState(false);

  const columns: GridColDef[] = [
    ...(isAdmin ? [{
      field: "actions",
      headerName: 'Actions',
      width: 130,
      renderCell: (params: GridRenderCellParams<PBMConnection>) => (
        <Box>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleFetchFiles(params.row);
            }}
            size="small"
            title="Fetch Files"
            color="primary"
            disabled={fetchingFiles}
          >
            {fetchingFiles ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(params.row);
            }}
            size="small"
            title="Edit"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(params.row);
            }}
            size="small"
            title="Delete"
            color="error"
          >
            <DeleteIcon />
          </IconButton>
          
        </Box>
      )
    }] : []),
    { field: 'ipAddress', headerName: 'Host', width: 150 },
    { field: 'userName', headerName: 'Username', width: 150 },
    { field: 'remoteSourceDirectory', headerName: 'Source Directory', width: 150 },
    { field: 'remoteDestinationDirectory', headerName: 'Destination Directory', width: 150 },
    { field: 'destinationContainerName', headerName: 'Container', width: 150 },
    { field: 'port', headerName: 'Port', width: 120 },
    { field: 'authType', headerName: 'Auth Type', width: 140 },
    {
      field: 'outputMappingId',
      headerName: 'Output Mapping',
      width: 200,
      renderCell: (params: GridRenderCellParams<PBMConnection>) => {
        const mapping = outputMappings.find(m => m.id === params.value);
        return mapping?.description || 'None';
      }
    }
  ];

  // Fetch current user and check if they have admin role
  const fetchCurrentUser = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      //setCurrentUser(user);
      setIsAdmin(user.role.name.toLowerCase() === 'admin');
    } catch (error) {
      console.error('Error fetching current user:', error);
      showFeedback('Failed to fetch user information', 'error');
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchConnections();
    fetchOutputMappings();
  }, [pbmId, fetchCurrentUser]);

  const fetchOutputMappings = async () => {
    try {
      const data = await getAllOutputMappings();
      setOutputMappings(data.sort((a, b) => a.description.localeCompare(b.description)));
    } catch (error) {
      showFeedback('Failed to fetch output mappings', 'error');
    }
  };

  const fetchConnections = async () => {
    try {
      const data = await getConnectionsByPbmId(pbmId);
      setConnections(data);
    } catch (error) {
      showFeedback('Failed to fetch connections', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFiles = async (connection: PBMConnection) => {
    if (!connection) return;
    
    try {
      setFetchingFiles(true);
      const result = await retrieveFilesByConnectionId(connection.id);
      
      // Show feedback to the user
      showFeedback(result.message, 'success');
      
      // If parent component provided a callback, pass the result to it
      if (onFilesRetrieved) {
        onFilesRetrieved(result);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      showFeedback(
        error instanceof Error ? error.message : 'Failed to retrieve files',
        'error'
      );
    } finally {
      setFetchingFiles(false);
    }
  }

  const handleEdit = (connection: PBMConnection) => {
    setEditingConnection(connection);
    setFormData({
      ipAddress: connection.ipAddress,
      userName: connection.userName,
      port: connection.port,
      keyLocation: connection.keyLocation,
      outputMappingId: connection.outputMappingId,
      authType: connection.authType,
      password: connection.password || '',
      keyPassphrase: connection.keyPassphrase || '',
      remoteDestinationDirectory: connection.remoteDestinationDirectory || '',
      remoteSourceDirectory: connection.remoteSourceDirectory || '',
      destinationContainerName: connection.destinationContainerName || ''
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      let connection: PBMConnection;

      if (editingConnection) {
        // First update the basic connection info with only non-sensitive fields
        const updateData: UpdatePBMConnectionDTO = {
          ipAddress: formData.ipAddress,
          userName: formData.userName,
          port: formData.port,
          remoteDestinationDirectory: formData.remoteDestinationDirectory,
          remoteSourceDirectory: formData.remoteSourceDirectory,
          destinationContainerName: formData.destinationContainerName,
          outputMappingId: formData.outputMappingId,
          authType: formData.authType,
          pbmId: editingConnection.pbmId
        };
        await updateConnection(editingConnection.id, updateData);

        // Then update credentials if they've changed
        if ((formData.authType === 'Password' && formData.password) ||
          (formData.authType === 'PrivateKey' && (formData.keyLocation || formData.keyPassphrase))) {
          await updateConnectionCredentials(editingConnection.id, {
            connectionId: editingConnection.id,
            authType: formData.authType,
            password: formData.password,
            keyLocation: formData.keyLocation,
            keyPassphrase: formData.keyPassphrase
          });
        }
      } else {
        // For new connections, first create the basic connection
        const createData: CreatePBMConnectionDTO = {
          ipAddress: formData.ipAddress,
          userName: formData.userName,
          port: formData.port,
          remoteDestinationDirectory: formData.remoteDestinationDirectory,
          remoteSourceDirectory: formData.remoteSourceDirectory,
          destinationContainerName: formData.destinationContainerName,
          outputMappingId: formData.outputMappingId,
          authType: formData.authType,
          pbmId
        };
        connection = await createConnection(createData);

        // Then set the credentials
        if ((formData.authType === 'Password' && formData.password) ||
          (formData.authType === 'PrivateKey' && (formData.keyLocation || formData.keyPassphrase))) {
          await updateConnectionCredentials(connection.id, {
            connectionId: connection.id,
            authType: formData.authType,
            password: formData.password,
            keyLocation: formData.keyLocation,
            keyPassphrase: formData.keyPassphrase
          });
        }
      }

      await fetchConnections();
      handleCloseDialog();
      showFeedback(
        `Connection ${editingConnection ? 'updated' : 'created'} successfully`,
        'success'
      );
    } catch (error) {
      showFeedback(
        `Failed to ${editingConnection ? 'update' : 'create'} connection`,
        'error'
      );
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingConnection(null);
    setFormData(initialFormData);
  };

  const handleDelete = (connection: PBMConnection) => {
    setConnectionToDelete(connection);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!connectionToDelete) return;

    try {
      await deleteConnection(connectionToDelete.id);
      await fetchConnections();
      showFeedback('Connection deleted successfully', 'success');
    } catch (error) {
      showFeedback('Failed to delete connection', 'error');
    } finally {
      setDeleteDialogOpen(false);
      setConnectionToDelete(null);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setConnectionToDelete(null);
  };

  const showFeedback = (message: string, severity: 'success' | 'error') => {
    setFeedback({
      open: true,
      message,
      severity
    });
  };

  const handleCloseFeedback = () => {
    setFeedback(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          PBM Connections
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            onClick={() => setOpenDialog(true)}
            disabled={connections.length > 0}
            title={connections.length > 0 ? "Only one connection allowed per PBM" : ""}
          >
            Add Connection
          </Button>
        )}
      </Box>
      <Box>
        <Paper sx={{ p: 2 }}>
          <DataGrid
            rows={connections}
            columns={columns}
            loading={loading}
            autoHeight
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
          />
        </Paper>

        <Dialog open={openDialog} onClose={handleCloseDialog}>
          <DialogTitle>
            {editingConnection ? 'Edit Connection' : 'Add New Connection'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Host"
                value={formData.ipAddress}
                onChange={(e) => setFormData(prev => ({ ...prev, ipAddress: e.target.value }))}
                required
              />
              <TextField
                fullWidth
                label="Username"
                value={formData.userName}
                onChange={(e) => setFormData(prev => ({ ...prev, userName: e.target.value }))}
                required
              />
              <TextField
                fullWidth
                label="Port"
                type="number"
                value={formData.port}
                onChange={(e) => setFormData(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                required
              />
              <TextField
                fullWidth
                label="Source Directory"
                value={formData.remoteSourceDirectory}
                onChange={(e) => setFormData(prev => ({ ...prev, remoteSourceDirectory: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Destination Directory"
                value={formData.remoteDestinationDirectory}
                onChange={(e) => setFormData(prev => ({ ...prev, remoteDestinationDirectory: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Container Name"
                value={formData.destinationContainerName}
                onChange={(e) => setFormData(prev => ({ ...prev, destinationContainerName: e.target.value }))}
              />
              <FormControl fullWidth>
                <InputLabel id="output-mapping-label">Output Mapping</InputLabel>
                <Select
                  labelId="output-mapping-label"
                  value={formData.outputMappingId}
                  label="Output Mapping"
                  onChange={(e) => setFormData(prev => ({ ...prev, outputMappingId: Number(e.target.value) }))}
                  required
                >
                  {outputMappings.map((mapping) => (
                    <MenuItem key={mapping.id} value={mapping.id}>
                      {mapping.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl component="fieldset">
                <FormLabel component="legend">Authentication Type</FormLabel>
                <RadioGroup
                  value={formData.authType}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    authType: e.target.value as 'Password' | 'PrivateKey',
                    // Clear irrelevant fields when switching auth type
                    password: e.target.value === 'Password' ? prev.password : '',
                    keyLocation: e.target.value === 'PrivateKey' ? prev.keyLocation : '',
                    keyPassphrase: e.target.value === 'PrivateKey' ? prev.keyPassphrase : ''
                  }))}
                >
                  <FormControlLabel value="Password" control={<Radio />} label="Password" />
                  <FormControlLabel value="PrivateKey" control={<Radio />} label="SSH Key" />
                </RadioGroup>
              </FormControl>

              {formData.authType === 'Password' && (
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              )}

              {formData.authType === 'PrivateKey' && (
                <>
                  <TextField
                    fullWidth
                    label="Key Location"
                    value={formData.keyLocation}
                    onChange={(e) => setFormData(prev => ({ ...prev, keyLocation: e.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="Key Passphrase"
                    type="password"
                    value={formData.keyPassphrase}
                    onChange={(e) => setFormData(prev => ({ ...prev, keyPassphrase: e.target.value }))}
                  />
                </>
              )}

              
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingConnection ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the connection to {connectionToDelete?.ipAddress}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={feedback.open}
          autoHideDuration={6000}
          onClose={handleCloseFeedback}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseFeedback}
            severity={feedback.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {feedback.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default PBMConnectionTable;
